import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Clock, Play, Pause, RotateCcw, Zap, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

interface RestraintSession {
  startTime: string;
  endTime?: string;
  urgeCount: number;
  isActive: boolean;
}

export function RestraintTimer() {
  const [session, setSession] = useState<RestraintSession | null>(null);
  const [elapsedHours, setElapsedHours] = useState(0);
  const [showUrgePrompt, setShowUrgePrompt] = useState(false);

  // Load active session
  useEffect(() => {
    const stored = localStorage.getItem('tension_restraint_session');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.isActive) {
        setSession(parsed);
      }
    }
  }, []);

  // Calculate elapsed time
  useEffect(() => {
    if (!session?.isActive) return;

    const interval = setInterval(() => {
      const start = new Date(session.startTime).getTime();
      const now = Date.now();
      const hours = (now - start) / (1000 * 60 * 60);
      setElapsedHours(hours);
    }, 1000);

    return () => clearInterval(interval);
  }, [session]);

  const startRestraint = () => {
    const newSession: RestraintSession = {
      startTime: new Date().toISOString(),
      urgeCount: 0,
      isActive: true,
    };
    setSession(newSession);
    localStorage.setItem('tension_restraint_session', JSON.stringify(newSession));
    toast.success('Restraint timer started. You\'ve got this! 💪');
  };

  const endRestraint = () => {
    if (!session) return;

    const completed = {
      ...session,
      endTime: new Date().toISOString(),
      isActive: false,
    };

    // Save to history
    const history = JSON.parse(localStorage.getItem('tension_restraint_history') || '[]');
    history.push(completed);
    localStorage.setItem('tension_restraint_history', JSON.stringify(history));
    localStorage.removeItem('tension_restraint_session');

    const hours = Math.floor(elapsedHours);
    toast.success(`Restraint session completed! ${hours} hours of strategic distance.`);
    setSession(null);
    setElapsedHours(0);
  };

  const logUrge = () => {
    if (!session) return;

    const updated = {
      ...session,
      urgeCount: session.urgeCount + 1,
    };
    setSession(updated);
    localStorage.setItem('tension_restraint_session', JSON.stringify(updated));
    setShowUrgePrompt(true);

    setTimeout(() => setShowUrgePrompt(false), 3000);
  };

  const getMilestone = () => {
    if (elapsedHours >= 72) return { text: '72+ hours!', color: 'text-green-600', impact: 'Tension typically drops 25-35 points' };
    if (elapsedHours >= 48) return { text: '48 hours!', color: 'text-green-600', impact: 'Tension typically drops 15-25 points' };
    if (elapsedHours >= 24) return { text: '24 hours!', color: 'text-blue-600', impact: 'Tension typically drops 8-15 points' };
    if (elapsedHours >= 12) return { text: '12 hours', color: 'text-yellow-600', impact: 'Keep going - impact compounds' };
    return { text: 'Building momentum', color: 'text-neutral-600', impact: 'Every hour counts' };
  };

  const milestone = getMilestone();

  if (!session?.isActive) {
    return (
      <Card className="p-6 bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-violet-100 rounded-lg">
              <Clock className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <h3 className="font-medium text-neutral-900">Practice Strategic Restraint</h3>
              <p className="text-sm text-neutral-600">Start a timed restraint session</p>
            </div>
          </div>

          <div className="bg-white/60 rounded-lg p-4 space-y-2 text-sm text-neutral-700">
            <p className="font-medium">What is restraint?</p>
            <p>
              Strategic restraint means intentionally reducing initiation to reset relationship dynamics.
              When tension is high, more pursuit creates more distance. Space allows natural reconnection.
            </p>
          </div>

          <Button onClick={startRestraint} className="w-full gap-2">
            <Play className="w-4 h-4" />
            Start Restraint Session
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
      <div className="space-y-4">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm mb-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
            Restraint Active
          </div>
          <div className="text-4xl font-light text-neutral-900 mb-1">
            {Math.floor(elapsedHours)}h {Math.floor((elapsedHours % 1) * 60)}m
          </div>
          <p className={`text-sm font-medium ${milestone.color}`}>{milestone.text}</p>
          <p className="text-xs text-neutral-600 mt-1">{milestone.impact}</p>
        </div>

        {/* Urge Counter */}
        <div className="bg-white/60 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-neutral-700">Urges resisted</span>
            <span className="text-2xl font-light text-neutral-900">{session.urgeCount}</span>
          </div>
          <Button
            onClick={logUrge}
            variant="outline"
            size="sm"
            className="w-full gap-2"
          >
            <Zap className="w-4 h-4" />
            Log Urge to Contact
          </Button>
          
          {showUrgePrompt && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 font-medium">✨ Urge logged!</p>
              <p className="text-xs text-green-700 mt-1">
                You just chose long-term connection over short-term anxiety relief. That's growth.
              </p>
            </div>
          )}
        </div>

        {/* Expected Impact */}
        <div className="bg-white/60 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <TrendingDown className="w-4 h-4 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-neutral-900">Expected Impact</p>
              <p className="text-xs text-neutral-600 mt-1">
                If restraint continues for {Math.ceil(48 - elapsedHours)} more hours, 
                RTI forecast: {Math.max(20, Math.floor(65 - (elapsedHours / 48) * 30))} points
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={endRestraint} variant="outline" className="flex-1 gap-2">
            <Pause className="w-4 h-4" />
            End Session
          </Button>
        </div>

        <p className="text-xs text-center text-neutral-500">
          Starting time: {new Date(session.startTime).toLocaleString()}
        </p>
      </div>
    </Card>
  );
}
